'use client'

import { AuthCard } from '@/components/Auth/AuthCard'
import { AuthFormField } from '@/components/Auth/AuthFormField'
import { LoadingButton } from '@/components/ui/loading-button'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required.').email('Please enter a valid email address.'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export const ForgotPasswordForm: React.FC = () => {
  const [sent, setSent] = useState(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    const response = await fetch(`/api/users/forgot-password`, {
      body: JSON.stringify({ email: data.email }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    })

    if (response.ok) {
      setSent(true)
    } else {
      setError('root', {
        message:
          'There was a problem while attempting to send you a password reset email. Please try again.',
      })
    }
  }

  return (
    <AuthCard
      title="Forgot password?"
      subtitle="Enter your email and we'll send you a reset link."
      showParamAlerts
      footer={
        <Link
          href="/login"
          className="font-medium text-brand-black underline underline-offset-2 hover:opacity-70 transition-opacity"
        >
          ← Back to Login
        </Link>
      }
    >
      {/* Success state — shown after submit */}
      {sent ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-2xl bg-green-50 border border-green-200 px-5 py-4 text-sm text-green-800 leading-relaxed"
        >
          If an account exists for that email address, we&apos;ve sent a password reset link. Please
          check your inbox (and spam folder).
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {errors.root && (
            <div
              role="alert"
              aria-live="assertive"
              className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-destructive"
            >
              {errors.root.message}
            </div>
          )}

          <div className="space-y-3">
            <div className="mb-3 last:mb-0">
              <AuthFormField
                id="forgot-email"
                label="Email"
                type="email"
                error={errors.email?.message}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={isSubmitting}
                {...register('email')}
              />
            </div>
          </div>

          <LoadingButton
            type="submit"
            loading={isSubmitting}
            loadingText="Sending…"
            className="w-full tracking-tight mt-2"
          >
            Send reset link
          </LoadingButton>
        </form>
      )}
    </AuthCard>
  )
}
